/**
 * Algolia search results component
 *
 * To be used inside a algolia search context (set by InstantSearch component)
 */
import { Link } from 'gatsby';
import { GatsbyImage, getImage } from 'gatsby-plugin-image';
// eslint-disable-next-line import/no-extraneous-dependencies
import { Hit, BaseHit } from 'instantsearch.js';
// eslint-disable-next-line import/no-extraneous-dependencies
import { MenuItem } from 'instantsearch.js/es/connectors/menu/connectMenu';
import * as React from 'react';
import {
  Highlight,
  Hits,
  Index,
  Pagination,
  Menu,
  useStats,
  useInstantSearch,
} from 'react-instantsearch-hooks-web';
import styled from 'styled-components';
import { algoliaIndexName } from '../../config';
import { AlgoliaRecord } from '../types';
import { cssClampValue } from '../utils/common';
import DefaultPostImage from './DefaultPostImage';
import SearchFilters from './SearchFilters';
import SearchContext from '../contexts/SearchContext';
import { PlainButton } from './styled-components';

const typeToLabel = {
  post: 'Article',
  podcast: 'Podcast',
  playlist: 'Playlist',
  recording: 'Recording',
};

const typeToRefinementLabel = {
  post: 'Articles',
  podcast: 'Podcasts',
  playlist: 'Playlists',
  recording: 'Recordings',
};

const Wrapper = styled.div`
  margin-bottom: ${cssClampValue(40, 80)};

  .ais-Hits-list {
    list-style: none;
    padding: 0;
    margin: 30px 0;
  }

  .ais-Pagination-list {
    display: flex;
    flex-wrap: wrap;
    align-items: center;
    gap: 10px;
    list-style: none;
    padding: 0;
    margin: 0;
  }

  .ais-Pagination-link {
    display: flex;
    align-items: center;
    justify-content: center;
    width: 50px;
    height: 50px;
    line-height: 1;
    border-radius: 10px;
    text-decoration: none;
    border: 1px solid var(--earth-fm--color--green-15);
    transition: background-color 0.2s ease-in-out;
  }

  a.ais-Pagination-link:hover,
  .ais-Pagination-item--selected a.ais-Pagination-link {
    color: var(--earth-fm--color--green);
    background-color: var(--earth-fm--color--green-15);
    border-color: var(--earth-fm--color--green);
  }

  .ais-Menu-list {
    list-style: none;
    display: flex;
    align-items: center;
    gap: 10px;
    margin: 0;
    padding: 0;
    overflow-x: auto;
  }

  .ais-Menu-item {
    .ais-Menu-link {
      display: block;
      border-radius: 20px;
      background-color: var(--earth-fm--color--green-05);
      padding: 6px 15px;
      font-size: 14px;
      line-height: 1;
      white-space: nowrap;
      text-decoration: none;
      transition: background-color 0.2s ease-in-out;

      &:focus-visible {
        outline-offset: -1px;
      }
    }

    &--selected {
      .ais-Menu-link {
        background-color: var(--earth-fm--color--green);
        color: #fff;
        font-weight: 600;
      }
    }
  }

  .ais-Menu-count {
    &::before {
      content: ' (';
    }

    &::after {
      content: ')';
    }
  }
`;

const ItemLink = styled(Link)`
  display: flex;
  padding: 14px;
  align-items: center;
  gap: 20px;
  border-radius: 10px;
  transition: background-color 0.2s ease-in-out;
  text-decoration: none;

  &:hover {
    background-color: var(--earth-fm--color--dark-beige);
  }
`;

const ItemContent = styled.div`
  display: flex;
  flex-direction: column;
`;

const ItemTitle = styled.div`
  margin: 0;
  font-size: 18px;
  font-weight: 700;
  line-height: 1.3;
  font-family: var(--earth-fm--font-family--jakarta);

  @media (min-width: 768px) {
    line-height: 1.4;
  }
`;

const ItemMeta = styled.div`
  display: flex;
  opacity: 0.5;
  transition: opacity 0.2s ease-in-out;
  gap: 3px;

  a {
    text-decoration: none;
  }

  ${ItemLink}:hover & {
    opacity: 1;
  }
`;

const ImgWrapper = styled.div`
  width: ${cssClampValue(48, 60)};
  height: ${cssClampValue(48, 60)};
  flex-shrink: 0;
  border-radius: ${cssClampValue(8, 10)};
  overflow: hidden;
  isolation: isolate;
`;

const MenuContainer = styled.div`
  display: flex;
  align-items: center;
  gap: 10px;
  margin-bottom: 10px;
`;

const ClearFiltersButton = styled(PlainButton)`
  font-size: 15px;
  line-height: 26px;
`;

const SearchResultContainer = styled.div<{ $isVisible: boolean }>`
  display: ${({ $isVisible }) => ($isVisible ? 'block' : 'none')};
`;

function PageHit({ hit }: { hit: Hit<BaseHit> }) {
  const { uri, title, image, type, meta } = hit as unknown as AlgoliaRecord;
  if (!uri || !title) {
    return null;
  }

  const gatsbyImage = getImage(image);

  return (
    <div>
      <ItemLink to={uri}>
        <ImgWrapper>
          {gatsbyImage ? <GatsbyImage image={gatsbyImage} alt="" /> : <DefaultPostImage alt="" />}
        </ImgWrapper>
        <ItemContent>
          <ItemTitle>
            <Highlight attribute="title" hit={hit} highlightedTagName="mark" />
          </ItemTitle>
          <ItemMeta>
            <span>
              {meta.recordists && Array.isArray(meta.recordists)
                ? meta.recordists.join(', ')
                : typeToLabel[type as keyof typeof typeToLabel]}
            </span>
          </ItemMeta>
        </ItemContent>
      </ItemLink>
    </div>
  );
}

interface Props {
  showTypeMenu?: boolean;
}

export default function SearchResults({ showTypeMenu = true }: Props) {
  const [initializing, setInitializing] = React.useState(true);
  const {
    isShowingFilters,
    setIsShowingFilters,
    isNavigatingWithKeyboard,
    setIsNavigatingWithKeyboard,
  } = React.useContext(SearchContext);
  const searchStats = useStats();
  const { indexUiState, setIndexUiState, status } = useInstantSearch();
  const [firstHit, setFirstHit] = React.useState<HTMLAnchorElement | null>(null);
  const menuRef = React.useRef<HTMLDivElement | null>(null);

  // show the search results when they are ready
  React.useEffect(() => {
    if (status === 'idle') {
      setInitializing(false);
    }
  }, [status, setInitializing]);

  const isShowingRecordings = React.useMemo(
    () => indexUiState?.menu?.type === 'recording',
    [indexUiState],
  );

  // show recordings with filters when the filter button is active
  React.useEffect(() => {
    if (isShowingFilters) {
      if (!indexUiState.menu) {
        setIndexUiState({
          ...indexUiState,
          menu: {
            type: 'recording',
          },
        });
      }
    } else if (indexUiState.menu?.type === 'recording') {
      setIndexUiState({
        ...indexUiState,
        menu: undefined,
      });
    }
  }, [indexUiState, isShowingFilters, setIndexUiState]);

  // reset menu and recording filters
  const handleFilterClear = React.useCallback(() => {
    setIsShowingFilters(false);
  }, [setIsShowingFilters]);

  // toggle recording filters when user clicks on a menu item
  const handleMenuClick = React.useCallback(
    (event: React.MouseEvent<HTMLDivElement, MouseEvent>) => {
      const isRecordingMenu =
        (event.target as HTMLElement)
          .closest('.ais-Menu-item')
          ?.innerHTML?.toLowerCase()
          ?.includes('recording') ?? false;

      // reset menu when user click on recording menu while it's already selected
      if (isShowingFilters && isRecordingMenu) {
        setIsShowingFilters(false);
        return;
      }

      // otherwise show filters when recording menu is clicked
      setIsShowingFilters(isRecordingMenu);
    },
    [isShowingFilters, setIsShowingFilters],
  );

  const transformItems = React.useCallback(
    (items: MenuItem[]) =>
      items.map((item) => ({
        ...item,
        label:
          typeToRefinementLabel[item.label as keyof typeof typeToRefinementLabel] || item.label,
      })),
    [],
  );

  const moveFocusToSearchInput = React.useCallback(
    (e: KeyboardEvent) => {
      const searchInput = document.querySelector<HTMLInputElement>('#header-search-field');
      if (e.key === 'ArrowUp' || (e.key === 'Tab' && e.shiftKey)) {
        e.preventDefault();
        searchInput?.focus();
        setIsNavigatingWithKeyboard(false);
      }
    },
    [setIsNavigatingWithKeyboard],
  );

  const handleFocus = React.useCallback(
    (event: React.FocusEvent<HTMLDivElement>) => {
      // if user presses arrow up or shift + tab on the first menu item,
      // move focus back to the search text field
      const firstMenuItem = menuRef.current?.querySelector('.ais-Menu-item:first-child a');
      if (firstMenuItem && event.target === firstMenuItem) {
        event.target.addEventListener('keydown', moveFocusToSearchInput);
        return () => {
          event.target.removeEventListener('keydown', moveFocusToSearchInput);
        };
      }

      return () => {};
    },
    [moveFocusToSearchInput],
  );

  // if user presses pagination buttons, get the first item of the search result page
  React.useEffect(() => {
    if (
      status === 'idle' &&
      !initializing &&
      isNavigatingWithKeyboard &&
      typeof document !== 'undefined'
    ) {
      setFirstHit(document.querySelector('.ais-Hits-item a') as HTMLAnchorElement | null);
    }
  }, [firstHit, initializing, isNavigatingWithKeyboard, status]);

  const handlePaginationTab = React.useCallback(
    (event: React.KeyboardEvent<HTMLDivElement>) => {
      const isLastPageButton =
        (event.target as HTMLElement)
          .closest('.ais-Pagination-link')
          ?.innerHTML?.toLowerCase()
          ?.includes('››') ?? false;

      // if user presses tab on the last page button(>>), focus to the first item of the search result page
      if (isLastPageButton && event.key === 'Tab' && !event.shiftKey) {
        firstHit?.focus();
      }

      // if user presses tab on pagination button when last page is selected,
      // focus to the first item of the search result page
      if (indexUiState?.page === searchStats.nbPages) {
        const isLastPage =
          (event.target as HTMLElement).closest('.ais-Pagination-link')?.innerHTML ===
          indexUiState?.page.toString();

        if (isLastPage) {
          event.preventDefault();
          firstHit?.focus();
        }
      }

      // if user presses enter key on pagination button, focus the first item of the search result page
      if (event.key === 'Enter' && firstHit) {
        firstHit.focus();
      }
    },
    [firstHit, indexUiState?.page, searchStats.nbPages],
  );

  return (
    <Wrapper>
      <Index indexName={algoliaIndexName}>
        {searchStats && searchStats.nbHits > 0 ? (
          <div>
            {showTypeMenu && (
              <MenuContainer ref={menuRef}>
                <Menu
                  attribute="type"
                  showMore={false}
                  sortBy={['count', 'name']}
                  transformItems={transformItems}
                  onClick={handleMenuClick}
                  onFocus={handleFocus}
                />
                {isShowingRecordings && (
                  <ClearFiltersButton onClick={handleFilterClear}>Clear filters</ClearFiltersButton>
                )}
              </MenuContainer>
            )}
            {isShowingRecordings && <SearchFilters />}
            {initializing && <div>Loading...</div>}
            <SearchResultContainer $isVisible={!initializing} aria-hidden={initializing}>
              <Hits hitComponent={PageHit} />
              <Pagination showFirst showLast onKeyUp={handlePaginationTab} />
            </SearchResultContainer>
          </div>
        ) : (
          <div>Nothing matched your search</div>
        )}
      </Index>
    </Wrapper>
  );
}
